/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ConnectedAutomations, Device } from '../model/device';
import { EDeviceCategory } from '../conf/device-categories';
import {
	AutomationsAliasData,
	CallsUnitLogResponse,
	CommandsData, DataRest, DeviceData, DeviceInfoData, DeviceModelData,
	AutomFwStatusData, DeviceSignalData, DeviceSpecialDaysData, DeviceSpecialDaysInfoData, DeviceStateData, DeviceTimersData, DeviceTimersInfoData, DevicesStatusData, FeaturesData, FirmwareResponseData, GateErrorsData, GateStatesData, GetDatetimeDeviceData, GetFirmwareGWVersionsData, GetFirmwareVersionsData, GroupData, GroupsResponseData, IdDataRest,
	ManualsData, ModelConfigStatesData, ModelStatesData, MtmaActionsData, MtmaChangesData, MtmaContactData,
	MtmaContactsResponseData, MtmaDiagnosticsLogResponse, MtmaDtmfData, MtmaDtmfsResponseData, MtmaFwAutoUpdateTimeslotsData, MtmaGroupsResponseData, MtmaKeypadData, MtmaKeypadsResponseData,
	MtmaExportListResponseData,
	MtmaModuleData, MtmaModulesData, MtmaPermissionsResponseData, MtmaRuleAccessData, MtmaRuleData, MtmaRulesResponseData, MtmaSettingsModulesResponse, MtmaSpecialPlanningsResponseData, MtmaTagData, MtmaTagsResponseData, MtmaTaskData,
	MtmaTimesliceData,
	MtmaTimeslicesResponseData, MtmaUnitData, MtmaUnitsResponseData, MtmaWeeklySchedulesResponseData, MtmaWriteAbortConfigData, NewDeviceModelData, PaginatedDeviceModels,
	PaginatedInstalledViews, PaginatedNotificationViews, PasswordChangeData, PasswordVerifyData, RbeAccessLogResponse, RemoteClientsData, SafetyInputCategoriesData, SettingsCategoryData,
	SettingsCategoryPagingData,
	SpecialPlanningData,
	SpecialPlanningsResponseData,
	WeeklyScheduleData,
	WeeklySchedulesResponseData
} from '../model/api/data-rest';
import { resources } from './api';
import { IOPayload } from '../model/io-payload';
import { EDeviceModel } from '../conf/device-models';
import { PaginationParams, toHttpParams } from '../model/api/pagination-params';
import { SettingsPayload } from '../model/settings-payload';
import { InvitationData } from '../model/api/invitation-data';
import { Command } from '../model/command';
import { MtmaModule, MtmaModuleConfig } from '../model/mtma-module';
import { MtmaContact } from '../model/mtma-contact';
import { MtmaUnit } from '../model/mtma-unit';
import { MtmaDtmf } from '../model/mtma-dtmf';
import { MtmaTag } from '../model/mtma-tag';
import { MtmaKeypad } from '../model/mtma-keypad';
import { MtmaFwAutoUpdateTimeslotWrite } from '../model/mtma-fw-autoupdate-timeslot';
import { PostFirmwareUpdate } from '../model/firmware-update';
import moment from 'moment';
import { DeviceSpecialDays, DeviceTimers } from '../model/device-timer';
import { MtmaRule, MtmaRuleAccess, MtmaTimeslice } from '../model/mtma-rules-access';
import { CAGroupRequest, CASpecialPlanning, CAWeeklySchedule } from '../model/control-access';
import { DeviceState } from '../model/device-status';
import { AutomationsAlias, AutomationsText } from '../model/automations-alias';


@Injectable({
	providedIn: 'root'
})
export class DeviceService {

	constructor(
		private http: HttpClient
	) { }

	addDevice(device: Device): Observable<IdDataRest> {
		switch (device.CategoryId) {
			case EDeviceCategory.Gateway:
				return this.http.post<IdDataRest>(
					`${environment.apiUrl}${resources.get('devices')}${resources.get('gateways')}/${device.Keycode}`,
					device
				);
			case EDeviceCategory.SubGateway:
				return this.http.post<IdDataRest>(
					`${environment.apiUrl}${resources.get('devices')}${resources.get('subgateways')}/${device.Keycode}`,
					device
				);
			case EDeviceCategory.Automation:
			case EDeviceCategory.Virtual:
				return this.http.post<IdDataRest>(`${environment.apiUrl}${resources.get('devices')}${resources.get('automations')}`, device);
		}
		return null;
	}

	getDevice(id: number): Observable<DeviceData> { // get single device by Id
		return this.http.get<DeviceData>(`${environment.apiUrl}${resources.get('devices')}/${id}`);
	}

	editDevice(device: Device): Observable<DataRest> {
		return this.http.put<DataRest>(`${environment.apiUrl}${resources.get('devices')}/${device.Id}`, device);
	}

	deleteDevice(deviceId: number): Observable<IdDataRest> {
		return this.http.delete<IdDataRest>(`${environment.apiUrl}${resources.get('devices')}/${deviceId}`);
	}

	getDevicesStatus(deviceIds: number[]): Observable<DevicesStatusData> {
		const params = new HttpParams().set('devices', '[' + deviceIds.toString() + ']');
		return this.http.get<DevicesStatusData>(`${environment.apiUrl}${resources.get('devicestatus')}`, { params });
	}

	getGatewayStatus(deviceId: number): Observable<AutomFwStatusData> {
		return this.http.get<AutomFwStatusData>(
			`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('status')}`
		);
	}

	getAutomationStatus(deviceId: number): Observable<AutomFwStatusData> {
		return this.http.get<AutomFwStatusData>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('status')}`
		);
	}

	getDeviceModel(modelId: number, language?: string): Observable<DeviceModelData> {
		let params: HttpParams = new HttpParams();
		if (language) { params = params.set('language', language); }
		return this.http.get<DeviceModelData>(
			`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('model')}/${modelId}${resources.get('info')}`,
			{ params }
		);
	}

	getNewDeviceModels(categoryId?: number): Observable<NewDeviceModelData> {
		let params: HttpParams = new HttpParams();
		if (categoryId) { params = params.set('categoryid', categoryId); }
		return this.http.get<NewDeviceModelData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('models')}`, { params });
	}

	getGateStates(language: string): Observable<GateStatesData> {
		const params = new HttpParams().set('language', language);
		return this.http.get<GateStatesData>(`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('gateState')}`, { params });
	}

	getGateErrors(language: string): Observable<GateErrorsData> {
		const params = new HttpParams().set('language', language);
		return this.http.get<GateErrorsData>(`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('gateError')}`, { params });
	}

	getConfigStates(modelId: number, language: string): Observable<ModelConfigStatesData> {
		const params = new HttpParams().set('language', language);
		return this.http.get<ModelConfigStatesData>(
			`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('model')}/${modelId}${resources.get('configurationStates')}`,
			{ params }
		);
	}

	initDevice(device: Device, terminals?: IOPayload[]): Observable<DataRest> {
		if (device.CategoryId === EDeviceCategory.Automation) {
			if (device.ParentId !== device.RootId) {
				return this.http.post<DataRest>(
					// eslint-disable-next-line max-len
					`${environment.apiUrl}${resources.get('gateways')}/${device.RootId}${resources.get('subgateways')}/${device.ParentId}${resources.get('automations')}/${device.Id}`,
					{}
				);
			} else {
				return this.http.post<DataRest>(
					`${environment.apiUrl}${resources.get('gateways')}/${device.RootId}${resources.get('automations')}/${device.Id}`,
					{}
				);
			}
		} else if (device.CategoryId === EDeviceCategory.SubGateway) {
			return this.http.post<DataRest>(
				`${environment.apiUrl}${resources.get('gateways')}/${device.ParentId}${resources.get('subgateways')}/${device.Id}`,
				{}
			);
		} else if (device.ModelId === EDeviceModel.MultiIO) { // multi IO
			return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('multiio')}/${device.Id}`, { Terminals: terminals });
		} else { // Gateway + First level devices
			return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('gateways')}/${device.Id}`, {});
		}
	}

	readSignalLevel(deviceId: number, categoryId: number): Observable<DeviceSignalData> { // read GSM signal level for gateway/subgateway
		if (categoryId === EDeviceCategory.Gateway) { // gateway
			return this.http.get<DeviceSignalData>(`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('signalLevel')}`);
		} else if (categoryId === EDeviceCategory.SubGateway) { // subgateway
			return this.http.get<DeviceSignalData>(`${environment.apiUrl}${resources.get('subgateways')}/${deviceId}${resources.get('signalLevel')}`);
		}
		return null;
	}

	getDeviceCommands(deviceId: number, modelId: number, lang: string): Observable<CommandsData> {
		const params = new HttpParams().set('language', lang);
		if (modelId === EDeviceModel.Output) { // OUTPUTS
			return this.http.get<CommandsData>(`${environment.apiUrl}${resources.get('outputs')}/${deviceId}${resources.get('commands')}`, { params });
		} else if (modelId === EDeviceModel.MultiIO) {
			return this.http.get<CommandsData>(`${environment.apiUrl}${resources.get('multiio')}/${deviceId}${resources.get('commands')}`, { params });
		} else { // AUTOMATIONS
			return this.http.get<CommandsData>(
				`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('commands')}`,
				{ params }
			);
		}
	}

	editDeviceCommands(deviceId: number, commands: Command[]): Observable<DataRest> {
		return this.http.post<DataRest>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('commands')}${resources.get('alias')}`,
			{ Commands: commands },
		);
	}

	executeCommand(deviceId: number, modelId: number, commandId: number): Observable<DataRest> {
		if (modelId === EDeviceModel.Output) { // OUTPUTS
			return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('outputs')}/${deviceId}${resources.get('commands')}/${commandId}`, {});
		} else if (modelId === EDeviceModel.MultiIO) { // MULTI IO OUTPUT
			return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('multiio')}/${deviceId}${resources.get('commands')}/${commandId}`, {});
		} else {
			return this.http.post<DataRest>(
				`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('commands')}/${commandId}`,
				{}
			);
		}
	}

	getAutomationFeatures(automationid: number): Observable<FeaturesData> {
		return this.http.get<FeaturesData>(`${environment.apiUrl}${resources.get('automations')}/${automationid}${resources.get('features')}`);
	}

	getDeviceInfo(deviceId: number, modelId: number, categoryId: number): Observable<DeviceInfoData> {
		if (modelId === EDeviceModel.Input) { // INPUTS
			return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('inputs')}/${deviceId}${resources.get('info')}`);
		} else if (modelId === EDeviceModel.Output) { // OUTPUTS
			return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('outputs')}/${deviceId}${resources.get('info')}`);
		} else if (modelId === EDeviceModel.MultiIO) {
			return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('multiio')}/${deviceId}${resources.get('info')}`);
		}
		switch (categoryId) {
			case EDeviceCategory.Gateway: // Gateway
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('info')}`);
			case EDeviceCategory.SubGateway: // SubGateway
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('subgateways')}/${deviceId}${resources.get('info')}`);
			case EDeviceCategory.Thermostat: // Thermostat
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('thermostats')}/${deviceId}${resources.get('info')}`);
			case EDeviceCategory.HomeAutomation: // HomeAutomation
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('homeautomations')}/${deviceId}${resources.get('info')}`);
			case EDeviceCategory.AlarmSystem: // AlarmSystem
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('alarmsystems')}/${deviceId}${resources.get('info')}`);
			case EDeviceCategory.IntercomSystem: // IntercomSystem
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('intercoms')}/${deviceId}${resources.get('info')}`);
			case EDeviceCategory.Automation: // Automation
				return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('info')}`);
		}
		return null;
	}

	getDeviceInfoGatewayByDeviceId(deviceId: number): Observable<DeviceInfoData> {
		return this.http.get<DeviceInfoData>(`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('info')}`);
	}

	getDeviceModels(
		language: string,
		pagination?: PaginationParams,
		categoryname?: string,
		categoryid?: number
	): Observable<PaginatedDeviceModels> {
		let params: HttpParams = new HttpParams();
		if (categoryname) { params = params.set('categoryname', categoryname); }
		if (categoryid) { params = params.set('categoryid', categoryid.toString()); }
		if (language) { params = params.set('language', language); }
		if (pagination) {
			params = params.set('size', pagination.size ? pagination.size.toString() : '0');
			params = params.set('page', pagination.page ? pagination.page.toString() : '1');
		}
		return this.http.get<PaginatedDeviceModels>(`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('models')}`, { params });
	}

	getModelSettings(modelId: number, language: string): Observable<SettingsCategoryData> {
		const params = new HttpParams().set('language', language);
		return this.http.get<SettingsCategoryData>(
			`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('model')}/${modelId}${resources.get('settings')}`,
			{ params }
		);
	}

	getDeviceSettings(deviceId: number, lang: string): Observable<SettingsCategoryData> {
		let params = new HttpParams();
		params = params.set('language', lang);
		return this.http.get<SettingsCategoryData>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('settings')}`,
			{ params }
		);
	}

	saveDeviceSettings(deviceId: number, settings: SettingsPayload): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('settings')}`, settings);
	}

	editMultiIO(device: Device, terminal: IOPayload): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('multiio')}/${device.Id}${resources.get('terminal')}`, terminal);
	}

	getRemoteClients(instanceId: number, userId?: number): Observable<RemoteClientsData> {
		let params;
		if (userId) {
			params = new HttpParams().set('userid', userId.toString());
		}
		return this.http.get<RemoteClientsData>(
			`${environment.apiUrl}${resources.get('gateways')}/${instanceId}${resources.get('remoteClients')}`,
			{ params }
		);
	}

	deleteRemoteClient(deviceId: number, slotId: string): Observable<DataRest> {
		return this.http.delete<DataRest>(
			`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('remoteClients')}/${slotId}`,
			{}
		);
	}

	toggleRemoteClientLock(deviceId: number, slotId: string, locked: boolean): Observable<DataRest> {
		return this.http.patch<DataRest>(
			`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('remoteClients')}/${slotId}`,
			{ Lock: locked }
		);
	}

	patchDeviceShare(deviceId: number, userId: number, maxRemotes: number): Observable<DataRest> {
		return this.http.patch<DataRest>(
			`${environment.apiUrl}${resources.get('gateways')}/${deviceId}${resources.get('share')}/${userId}`,
			{ RemotesMax: maxRemotes }
		);
	}

	shareDevice(email: string, firstName: string, lastName: string, deviceId: number, remotesMax: number): Observable<DataRest> {
		const payload = {
			Email: email,
			FirstName: firstName,
			LastName: lastName,
			DeviceId: deviceId,
			RemotesMax: remotesMax
		};
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('devices')}${resources.get('share')}`, payload);
	}

	removeShareDevice(deviceId: number, userId?: number): Observable<DataRest> {
		let params = new HttpParams()
			.append('deviceId', deviceId.toString());
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('devices')}${resources.get('delete')}${resources.get('share')}`, { params });
	}

	confirmShareDevice(payload: InvitationData): Observable<DataRest> {
		let params = new HttpParams()
			.append('deviceId', payload.DeviceId.toString())
			.append('ownerId', payload.OwnerId.toString())
			.append('remotesMax', payload.RemotesMax.toString())
			.append('verification', payload.Verification);
		if (payload.UserId) {
			params = params.append('userId', payload.UserId.toString());
			return this.http.post<DataRest>(
				`${environment.apiUrl}${resources.get('devices')}${resources.get('confirm')}${resources.get('share')}${resources.get('onSignup')}`,
				{},
				{ params }
			);
		}
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('devices')}${resources.get('confirm')}${resources.get('share')}`, {}, { params });
	}

	getDeviceModelManuals(modelId: number, language?: string): Observable<ManualsData> {
		let params: HttpParams = new HttpParams();
		if (language) { params = params.set('language', language); }
		return this.http.get<ManualsData>(
			`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('model')}/${modelId}${resources.get('docs')}`,
			{ params }
		);
	}

	getErrorCodes(language: string): Observable<ModelStatesData> {
		const params = new HttpParams().set('language', language);
		return this.http.get<ModelStatesData>(`${environment.apiUrl}${resources.get('deviceModel')}${resources.get('errorCodes')}`, { params });
	}

	getSafetyInputs(deviceId: number): Observable<SafetyInputCategoriesData> {
		return this.http.get<SafetyInputCategoriesData>(
			`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('safetyInputs')}`
		);
	}

	getInstalledViews(paging?: PaginationParams, filter?: string, userId?: number, siteId?: number): Observable<PaginatedInstalledViews> {
		let params = new HttpParams();
		if (paging) {
			params = toHttpParams(paging);
		}
		if (filter) {
			params = params.append('filter', filter);
		}
		if (userId) {
			params = params.set('userId', userId.toString());
		}
		if (siteId) {
			params = params.set('siteId', siteId.toString());
		}
		return this.http.get<PaginatedInstalledViews>(`${environment.apiUrl}${resources.get('devices')}${resources.get('installedView')}`, { params });
	}

	getMtmaActions(deviceId: number): Observable<MtmaActionsData> {
		return this.http.get<MtmaActionsData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('actions')}`);
	}

	getMtmaModules(deviceId: number): Observable<MtmaModulesData> {
		return this.http.get<MtmaModulesData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('modules')}`);
	}
	getMtmaModule(deviceId: number, moduleId: number): Observable<MtmaModuleData> {
		return this.http.get<MtmaModuleData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('modules')}/${moduleId}`);
	}
	addMtmaModule(deviceId: number, module: MtmaModule): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('modules')}`, { ...module });
	}
	editMtmaModule(deviceId: number, module: MtmaModule): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('modules')}/${module.Id}`, module);
	}
	deleteMtmaModule(deviceId: number, moduleId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('modules')}/${moduleId}`);
	}
	editParamMtmaModule(deviceId: number, module: MtmaModuleConfig): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('writeparams')}`, module);
	}

	getMtmaContacts(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaContactsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaContactsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('contacts')}`, { params });
	}
	getMtmaContact(deviceId: number, contactId: string): Observable<MtmaContactData> {
		return this.http.get<MtmaContactData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('contacts')}/${contactId}`);
	}
	addMtmaContact(deviceId: number, contact: MtmaContact): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('contacts')}`, { ...contact });
	}
	editMtmaContact(deviceId: number, contact: MtmaContact): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('contacts')}/${contact.ObjId}`, contact);
	}
	deleteMtmaContact(deviceId: number, contactId: string): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('contacts')}/${contactId}`);
	}

	postMtmaDiagnosticsState(deviceId: number): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('state')}`, {});
	}

	getMtmaUnits(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaUnitsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaUnitsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('units')}`, { params });
	}
	getMtmaUnit(deviceId: number, unitId: string): Observable<MtmaUnitData> {
		return this.http.get<MtmaUnitData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('units')}/${unitId}`);
	}
	addMtmaUnit(deviceId: number, unit: MtmaUnit): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('units')}`, { ...unit });
	}
	editMtmaUnit(deviceId: number, unit: MtmaUnit): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('units')}/${unit.ObjId}`, unit);
	}
	deleteMtmaUnit(deviceId: number, unitId: string): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('units')}/${unitId}`);
	}

	getMtmaDtmfs(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaDtmfsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaDtmfsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('dtmftones')}`, { params });
	}
	getMtmaDtmf(deviceId: number, dtmfId: number): Observable<MtmaDtmfData> {
		return this.http.get<MtmaDtmfData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('dtmftones')}/${dtmfId}`);
	}
	addMtmaDtmf(deviceId: number, dtmf: MtmaDtmf): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('dtmftones')}`, { ...dtmf });
	}
	editMtmaDtmf(deviceId: number, dtmf: MtmaDtmf): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('dtmftones')}/${dtmf.ObjId}`, dtmf);
	}
	deleteMtmaDtmf(deviceId: number, dtmfId: string): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('dtmftones')}/${dtmfId}`);
	}


	getMtmaTags(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaTagsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaTagsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tags')}`, { params });
	}
	getMtmaTag(deviceId: number, tagId: number): Observable<MtmaTagData> {
		return this.http.get<MtmaTagData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tags')}/${tagId}`);
	}
	addMtmaTag(deviceId: number, tag: MtmaTag): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tags')}`, { ...tag });
	}
	editMtmaTag(deviceId: number, tag: MtmaTag): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tags')}/${tag.ObjId}`, tag);
	}
	deleteMtmaTag(deviceId: number, tagId: string): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tags')}/${tagId}`);
	}


	getMtmaKeypads(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaKeypadsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaKeypadsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('keypads')}`, { params });
	}
	getMtmaKeypad(deviceId: number, keypadId: number): Observable<MtmaKeypadData> {
		return this.http.get<MtmaKeypadData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('keypads')}/${keypadId}`);
	}
	addMtmaKeypad(deviceId: number, keypad: MtmaKeypad): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('keypads')}`, { ...keypad });
	}
	editMtmaKeypad(deviceId: number, keypad: MtmaKeypad): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('keypads')}/${keypad.ObjId}`, keypad);
	}
	deleteMtmaKeypad(deviceId: number, keypadId: string): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('keypads')}/${keypadId}`);
	}
	getMtmaSettingsModules(): Observable<MtmaSettingsModulesResponse> {
		return this.http.get<MtmaSettingsModulesResponse>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}${resources.get('modules')}${resources.get('config')}`);
	}
	getMtmaChanges(deviceId: number): Observable<MtmaChangesData> {
		return this.http.get<MtmaChangesData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('changes')}`);
	}
	getMtmaActiveTask(deviceId: number): Observable<MtmaTaskData> {
		return this.http.get<MtmaTaskData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tasks')}${resources.get('active')}`);
	}
	mtmaWriteConfig(deviceId: number): Observable<MtmaWriteAbortConfigData> {
		return this.http.post<MtmaWriteAbortConfigData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('writeconfig')}`, {});
	}
	mtmaAbortConfig(deviceId: number): Observable<MtmaWriteAbortConfigData> {
		return this.http.post<MtmaWriteAbortConfigData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('tasks')}${resources.get('abort')}`, {});
	}

	// Mtma Timeslice
	getMtmaTimeslices(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaTimeslicesResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaTimeslicesResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('timeslices')}`, { params });
	}
	getMtmaTimeslice(deviceId: number, timeslicesId: number): Observable<MtmaTimesliceData> {
		return this.http.get<MtmaTimesliceData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('timeslices')}/${timeslicesId}`);
	}
	deleteMtmaTimeslice(deviceId: number, timeslicesId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('timeslices')}/${timeslicesId}`);
	}

	// Mtma Rules
	getMtmaRules(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaRulesResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaRulesResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}`, { params });
	}
	getMtmaRule(deviceId: number, ruleId: number): Observable<MtmaRuleData> {
		return this.http.get<MtmaRuleData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${ruleId}`);
	}
	addMtmaRule(deviceId: number, rule: MtmaRule): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}`, { ...rule });
	}
	editMtmaRule(deviceId: number, rule: MtmaRule): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${rule.Id}`, rule);
	}
	deleteMtmaRule(deviceId: number, ruleId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${ruleId}`);
	}

	// Mtma Rules Access
	getMtmaRuleAccess(deviceId: number, ruleId: number): Observable<MtmaRuleAccessData> {
		return this.http.get<MtmaRuleAccessData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${ruleId}${resources.get('titles')}`);
	}
	editMtmaRuleAccess(deviceId: number, ruleId: number, ruleAccess: MtmaRuleAccess): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${ruleId}${resources.get('titles')}`, ruleAccess);
	}

	cloneMtma(userId: number, deviceIdFrom: number, deviceIdTo: number, isImpersMode: boolean): Observable<DataRest> {
		let params: HttpParams;
		params = new HttpParams();
		if (isImpersMode) {
			params = params.set('userid', userId);
		}
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('mtma')}/${deviceIdFrom}${resources.get('replace')}/${deviceIdTo}`, {}, { params });
	}

	getDatetimeDevice(deviceId: number): Observable<GetDatetimeDeviceData> {
		return this.http.get<GetDatetimeDeviceData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('datetime')}`);
	}

	//----------------- FIRMWARE MTMA
	getFirmwareUpdate(deviceId: number, test: boolean): Observable<GetFirmwareVersionsData> {
		let params: HttpParams = new HttpParams();
		if (test) { params = params.set('stage', 'test'); }
		return this.http.get<GetFirmwareVersionsData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('updates')}`,
			{ params });
	}
	//----------------- FIRMWARE MTMA
	//----------------- FIRMWARE GW
	getGWFirmwareUpdate(deviceId: number, test: boolean): Observable<GetFirmwareGWVersionsData> {
		let params: HttpParams = new HttpParams();
		if (test) { params = params.set('stage', 'test'); }
		return this.http.get<GetFirmwareGWVersionsData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('updates')}`,
			{ params });
	}
	//----------------- FIRMWARE GW

	postFirmwareUpdate(deviceId: number, firmware: PostFirmwareUpdate, test: boolean): Observable<DataRest> {
		let params: HttpParams = new HttpParams();
		if (test) { params = params.set('stage', 'test'); }
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('updates')}`, firmware,
			{ params });
	}

	getRbeAccessLog(deviceId: number, startDate: Date, endDate: Date, filter?: string,
		typeAction?: number, result?: number, sort?: string, order?: string, size?: number, page?: number,
		output?: string, actiondevicename?: string, commands?: number[]):
		Observable<RbeAccessLogResponse> {
		let params = new HttpParams();
		params = params.set('startDate', moment(startDate).format('YYYY-MM-DDTHH:mm:ss'));
		params = params.set('endDate', moment(endDate).format('YYYY-MM-DDTHH:mm:ss'));
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('source', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (typeAction && typeAction !== 0) {
			params = params.set('typeAction', typeAction.toString());
		}
		if (result && result !== 0) {
			params = params.set('result', result.toString());
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		if (output) {
			params = params.set('output', output);
		}
		if (actiondevicename) {
			params = params.set('actiondevicename', actiondevicename);
		}
		if (commands) {
			params = params.set('commands', '[' + commands.toString() + ']');
		}
		return this.http.get<RbeAccessLogResponse>(`${environment.apiUrl}${resources.get('v1')}${resources.get('accesslog')}${resources.get('data')}${resources.get('device')}/${deviceId}`, { params });
	}
	getAccessLog(deviceId: number, startDate: Date, endDate: Date, filter?: string,
		typeAction?: number, result?: number, sort?: string, order?: string, size?: number, page?: number):
		Observable<RbeAccessLogResponse> {
		let params = new HttpParams();
		params = params.set('startDate', moment(startDate).format('YYYY-MM-DDTHH:mm:ss'));
		params = params.set('endDate', moment(endDate).format('YYYY-MM-DDTHH:mm:ss'));
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('source', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (typeAction && typeAction !== 0) {
			params = params.set('typeAction', typeAction.toString());
		}
		if (result && result !== 0) {
			params = params.set('result', result.toString());
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<RbeAccessLogResponse>(`${environment.apiUrl}${resources.get('v1')}${resources.get('accesslog')}${resources.get('data')}${resources.get('device')}/${deviceId}`, { params });
	}

	getCallUnitEventLog(deviceId: number, startDate: Date, endDate: Date, unitName?: string,
		sort?: string, order?: string, size?: number, page?: number):
		Observable<CallsUnitLogResponse> {
		let params = new HttpParams();
		params = params.set('startDate', moment(startDate).format('YYYY-MM-DDTHH:mm:ss'));
		params = params.set('endDate', moment(endDate).format('YYYY-MM-DDTHH:mm:ss'));
		if (sort) {
			params = params.set('sort', sort);
		}
		if (unitName) {
			params = params.set('unitName', unitName);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<CallsUnitLogResponse>(`${environment.apiUrl}${resources.get('v1')}${resources.get('calllog')}${resources.get('data')}${resources.get('device')}/${deviceId}`, { params });
	}

	getMtmaDiagnosticsLog(deviceId: number, startDate: Date, endDate: Date, eventType?: number, eventSubType?: number,
		sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaDiagnosticsLogResponse> {
		let params = new HttpParams();
		params = params.set('startDate', moment(startDate).format('YYYY-MM-DDTHH:mm:ss'));
		params = params.set('endDate', moment(endDate).format('YYYY-MM-DDTHH:mm:ss'));
		if (eventType != null) {
			params = params.set('type', eventType.toString());
		}
		if (eventSubType != null) {
			params = params.set('subType', eventSubType.toString());
		}
		if (sort) {
			params = params.set('sort', sort);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		return this.http.get<MtmaDiagnosticsLogResponse>(`${environment.apiUrl}${resources.get('v1')}${resources.get('diagnostics')}${resources.get('data')}${resources.get('device')}/${deviceId}`, { params });
	}

	getMtmaFwAutoUpdateTimeslots(deviceId: number, userId: number): Observable<MtmaFwAutoUpdateTimeslotsData> {
		const params = new HttpParams().set('userId', userId.toString());
		return this.http.get<MtmaFwAutoUpdateTimeslotsData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('deviceFwAutoUpdateTimeslots')}`,
			{ params },
		);
	}

	postMtmaFwAutoUpdateTimeslots(deviceId: number, userId: number, body: MtmaFwAutoUpdateTimeslotWrite): Observable<MtmaFwAutoUpdateTimeslotsData> {
		const params = new HttpParams().set('userId', userId.toString());
		return this.http.post<MtmaFwAutoUpdateTimeslotsData>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('deviceFwAutoUpdateTimeslots')}`,
			body,
			{ params },
		);
	}

	deleteMtmaFwAutoUpdateTimeslots(deviceId: number, userId: number): Observable<DataRest> {
		const params = new HttpParams().set('userId', userId.toString());
		return this.http.delete<DataRest>(
			`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('deviceFwAutoUpdateTimeslots')}`,
			{ params },
		);
	}

	getAutomationDiscover(id: number): Observable<DataRest> { // get single device by Id
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${id}${resources.get('discover')}`, {});
	}

	getDeviceTimersInfo(deviceId: number): Observable<DeviceTimersInfoData> {
		return this.http.get<DeviceTimersInfoData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('info')}`);
	}
	getDeviceTimers(deviceId: number): Observable<DeviceTimersData> {
		return this.http.get<DeviceTimersData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}`);
	}
	addDeviceTimer(deviceId: number, timer: DeviceTimers): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}`, { ...timer });
	}
	editDeviceTimer(deviceId: number, timer: DeviceTimers): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}`, { ...timer });
	}
	deleteDeviceTimer(deviceId: number, position: number): Observable<DataRest> {
		const params = new HttpParams().set('position', position);
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}`, { params });
	}
	deleteDeviceAllTimer(deviceId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}`);
	}



	getDeviceSpecialDaysInfo(deviceId: number): Observable<DeviceSpecialDaysInfoData> {
		return this.http.get<DeviceSpecialDaysInfoData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('specialdays')}${resources.get('info')}`);
	}
	getDeviceSpecialDays(deviceId: number, position: number): Observable<DeviceSpecialDaysData> {
		const params = new HttpParams().set('positionStart', position);
		return this.http.get<DeviceSpecialDaysData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('specialdays')}`, { params });
	}
	addDeviceSpecialDay(deviceId: number, specialDay: DeviceSpecialDays): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('specialdays')}`, { ...specialDay });
	}
	editDeviceSpecialDay(deviceId: number, specialDay: DeviceSpecialDays): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('specialdays')}`, { ...specialDay });
	}
	deleteDeviceSpecialDay(deviceId: number, position: number): Observable<DataRest> {
		const params = new HttpParams().set('position', position);
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('specialdays')}`, { params });
	}
	deleteDeviceAllSpecialDay(deviceId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('timers')}${resources.get('specialdays')}`);
	}
	editNameAutomationConnected(deviceId: number, automation: ConnectedAutomations): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('connectedautomations')}`, { ConnectedAutomations: [automation] });
	}


	getStateDeviceByCommandId(deviceId: number, commandId: number): Observable<DeviceStateData> {
		return this.http.get<DeviceStateData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('states')}/${commandId}`);
	}
	getNewStateDeviceByCommandId(deviceId: number, commandId: number): Observable<DeviceState> {
		return this.http.get<DeviceState>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}/${deviceId}${resources.get('states')}/${commandId}`);
	}
	passwordVerify(deviceId: number, password: string): Observable<PasswordVerifyData> {
		return this.http.post<PasswordVerifyData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('password')}${resources.get('verify')}`, { Password: password });
	}
	passwordChange(deviceId: number, passwordOld: string, passwordNew: string): Observable<PasswordChangeData> {
		return this.http.patch<PasswordChangeData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('password')}`, { PasswordOld: passwordOld, PasswordNew: passwordNew });
	}

	//---------------------------- Control Access
	//
	getGroups(deviceId: number, size?: number, page?: number, filter?: string, sort?: string, order?: string):
		Observable<GroupsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('likename', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<GroupsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('groups')}`, { params });
	}
	getGroup(deviceId: number, groupId: number): Observable<GroupData> {
		return this.http.get<GroupData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('groups')}/${groupId}`);
	}
	addGroup(deviceId: number, group: CAGroupRequest): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('groups')}`, { ...group });
	}
	editGroup(deviceId: number, group: CAGroupRequest): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('groups')}/${group.Id}`, group);
	}
	deleteGroup(deviceId: number, groupId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('groups')}/${groupId}`);
	}

	// WeeklySchedules
	getWeeklySchedules(deviceId: number, likeName?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<WeeklySchedulesResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (likeName) {
			params = params.set('likename', likeName);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<WeeklySchedulesResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('weeklyschedules')}`, { params });
	}
	getWeeklySchedule(deviceId: number, weeklyScheduleId: number): Observable<WeeklyScheduleData> {
		return this.http.get<WeeklyScheduleData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('weeklyschedules')}/${weeklyScheduleId}`);
	}
	addWeeklySchedule(deviceId: number, weeklySchedule: CAWeeklySchedule): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('weeklyschedules')}`, { ...weeklySchedule });
	}
	editWeeklySchedule(deviceId: number, weeklySchedule: CAWeeklySchedule): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('weeklyschedules')}/${weeklySchedule.Id}`, weeklySchedule);
	}
	deleteWeeklySchedule(deviceId: number, weeklyScheduleId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('weeklyschedules')}/${weeklyScheduleId}`);
	}

	// WeeklySchedules
	getSpecialPlannings(deviceId: number, likeName?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<SpecialPlanningsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (likeName) {
			params = params.set('likename', likeName);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<SpecialPlanningsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('specialplannings')}`, { params });
	}
	getSpecialPlanning(deviceId: number, specialPlanningId: number): Observable<SpecialPlanningData> {
		return this.http.get<SpecialPlanningData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}/${deviceId}${resources.get('device')}${resources.get('specialplannings')}/${specialPlanningId}`);
	}
	addSpecialPlanning(deviceId: number, specialPlanning: CASpecialPlanning): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('specialplannings')}`, { ...specialPlanning });
	}
	editSpecialPlanning(deviceId: number, specialPlanning: CASpecialPlanning): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('specialplannings')}/${specialPlanning.Id}`, specialPlanning);
	}
	deleteSpecialPlanning(deviceId: number, specialPlanningId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('specialplannings')}/${specialPlanningId}`);
	}

	//---------------------------- Control Access

	// getLogThExport(keycode: string, startDate: Date, endDate: Date, metrics: string[]):
	//     Observable<LogThExportData> {
	//     let params = new HttpParams();
	//     /*params = params.set('startDate', moment(startDate).format('YYYY-MM-DDTHH:mm:ss'));
	//     params = params.set('endDate', moment(endDate).format('YYYY-MM-DDTHH:mm:ss'));*/
	//     params = params.set('from', moment(startDate).toISOString().replace('.000', ''));
	//     params = params.set('to', moment(endDate).toISOString().replace('.000', ''));
	//     if (metrics) {
	//         params = params.set('metrics', metrics.toString());
	//     }
	//     return this.http.get<LogThExportData>(`${environment.apiUrlThermo}${resources.get('api')}${resources.get('v1')}${resources.get('statistics')}/${keycode}${resources.get('download')}`, { params });
	// }

	//SETTINGS PAGING
	getDeviceSettingsPaging(deviceId: number, lang: string, page: number): Observable<SettingsCategoryPagingData> {
		let params = new HttpParams();
		params = params.set('language', lang);
		params = params.set('page', page);
		return this.http.get<SettingsCategoryPagingData>(
			`${environment.apiUrl}${resources.get('v2')}${resources.get('automations')}/${deviceId}${resources.get('settings')}`,
			{ params }
		);
	}
	//SETTINGS PAGING
	acRbeSync(deviceId: number): Observable<DataRest> {
		return this.http.get<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('acinfo')}${resources.get('sync')}`);
	}
	acRbeReset(deviceId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('controlaccess')}${resources.get('device')}/${deviceId}${resources.get('acinfo')}`);
	}

	// TH log
	// getThLog(keycode: string, from?: Date, intervaltype?: string, location?: string):
	//     Observable<GetThLogResponseData> {
	//     let params = new HttpParams();
	//     if (from) {
	//         params = params.set('from', from.toISOString());
	//     }
	//     if (intervaltype) {
	//         params = params.set('intervaltype', intervaltype);
	//     }
	//     if (location) {
	//         params = params.set('location', location);
	//     }
	//     return this.http.get<GetThLogResponseData>(`${environment.apiUrlThermo}${resources.get('api')}${resources.get('v1')}${resources.get('statistics')}/${keycode}`, { params });
	// }

	//---------------------------- Control Access
	//
	// Mtma Group -> is not sortable BE right now
	getMtmaGroups(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaGroupsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaGroupsResponseData>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('groups')}`, { params });
	}
	getMtmaGroup(deviceId: number, groupId: number): Observable<MtmaRuleData> {
		return this.http.get<MtmaRuleData>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('group')}/${groupId}`);
	}
	addMtmaGroup(deviceId: number, rule: MtmaRule): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('group')}`, { ...rule });
	}
	editMtmaGroup(deviceId: number, rule: MtmaRule): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('group')}/${rule.Id}`, rule);
	}
	deleteMtmaGroup(deviceId: number, ruleId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('group')}/${ruleId}`);
	}

	// Mtma Rules Access
	getMtmaGroupAccess(deviceId: number, ruleId: number): Observable<MtmaRuleAccessData> {
		return this.http.get<MtmaRuleAccessData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${ruleId}${resources.get('titles')}`);
	}
	editMtmaGroupAccess(deviceId: number, ruleId: number, ruleAccess: MtmaRuleAccess): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('acrules')}/${ruleId}${resources.get('titles')}`, ruleAccess);
	}

	// Mtma Weekly Schedules
	getMtmaWeeklySchedules(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaWeeklySchedulesResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaWeeklySchedulesResponseData>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('weeklyschedules')}`, { params });
	}
	getMtmaWeeklySchedule(deviceId: number, timeslicesId: number): Observable<MtmaTimesliceData> {
		return this.http.get<MtmaTimesliceData>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('weeklyschedule')}/${timeslicesId}`);
	}
	addMtmaWeeklySchedule(deviceId: number, timeslices: MtmaTimeslice): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('weeklyschedule')}`, { ...timeslices });
	}
	editMtmaWeeklySchedule(deviceId: number, timeslices: MtmaTimeslice): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('weeklyschedule')}/${timeslices.Id}`, timeslices);
	}
	deleteMtmaWeeklySchedule(deviceId: number, timeslicesId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('weeklyschedule')}/${timeslicesId}`);
	}

	// Mtma Special Plannings
	getMtmaSpecialPlannings(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaSpecialPlanningsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaSpecialPlanningsResponseData>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('specialplannings')}`, { params });
	}
	getMtmaSpecialPlanning(deviceId: number, timeslicesId: number): Observable<MtmaTimesliceData> {
		return this.http.get<MtmaTimesliceData>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('specialplanning')}/${timeslicesId}`);
	}
	addMtmaSpecialPlanning(deviceId: number, timeslices: MtmaTimeslice): Observable<DataRest> {
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('specialplanning')}`, { ...timeslices });
	}
	editMtmaSpecialPlanning(deviceId: number, timeslices: MtmaTimeslice): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('specialplanning')}/${timeslices.Id}`, timeslices);
	}
	deleteMtmaSpecialPlanning(deviceId: number, timeslicesId: number): Observable<DataRest> {
		return this.http.delete<DataRest>(`${environment.apiUrl}${resources.get('v2')}${resources.get('mtma')}/${deviceId}${resources.get('specialplanning')}/${timeslicesId}`);
	}

	//---------------------------- Control Access

	updateDeviceAutomationRtc(deviceId: number): Observable<DataRest> {
		const params = new HttpParams();
		return this.http.post<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('setrtc')}`, params);
	}
	getAutomationsFirmwares(test: boolean):
		Observable<FirmwareResponseData> {
		let params: HttpParams = new HttpParams();
		if (test) { params = params.set('test', true); }
		return this.http.get<FirmwareResponseData>(`${environment.apiUrl}${resources.get('firmwares')}`, { params });
	}

	getAutomationsAlias(deviceId: number): Observable<AutomationsAliasData> {
		const params = new HttpParams();
		return this.http.get<AutomationsAliasData>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('alias')}`, { params });
	}

	editAutomationsAlias(deviceId: number, automationsAlias: AutomationsAlias): Observable<DataRest> {
		return this.http.patch<DataRest>(`${environment.apiUrl}${resources.get('automations')}/${deviceId}${resources.get('alias')}`, automationsAlias);
	}

	getAutomationsText(lang: string): Observable<AutomationsText> {
		const params = new HttpParams();
		return this.http.get<AutomationsText>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('tr')}/${lang}`, { params });
	}

	getMtmaPermissions(deviceId: number, filter?: string, sort?: string, order?: string, size?: number, page?: number):
		Observable<MtmaPermissionsResponseData> {
		let params = new HttpParams();
		if (sort) {
			params = params.set('sort', sort);
		}
		if (filter) {
			params = params.set('filter', filter);
		}
		if (order) {
			params = params.set('order', order);
		}
		if (size) {
			params = params.set('size', size.toString());
		}
		if (page) {
			params = params.set('page', page.toString());
		}
		return this.http.get<MtmaPermissionsResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}/${deviceId}${resources.get('permissions')}`, { params });
	}

	getMtmaExportListCsv(exportListId: number, userId?: number): Observable<MtmaExportListResponseData> {
		let params;
		if (userId) {
			params = new HttpParams().set('userId', userId.toString());
		}
		return this.http.get<MtmaExportListResponseData>(`${environment.apiUrl}${resources.get('v1')}${resources.get('mtma')}${resources.get('exports')}/${exportListId}`, { params });
	}

	getNotificationView(paging?: PaginationParams, filter?: string, acls?: number[]): Observable<PaginatedNotificationViews> {
		let params = new HttpParams();
		if (paging) {
			params = toHttpParams(paging);
		}
		if (filter) {
			params = params.append('filter', filter);
		}
		if (acls) {
			// params = params.append('acls', String(acls)); // acls=0,1,2
			params = params.append('acls', `[${acls.join(',')}]`); // acls=[0,1,2]
		}
		return this.http.get<PaginatedNotificationViews>(`${environment.apiUrl}${resources.get('v1')}${resources.get('devices')}${resources.get('notificationView')}`, { params });
	}
}
